import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Firestore, getDoc, doc } from "@angular/fire/firestore";
import { BehaviorSubject, filter, firstValueFrom, map } from "rxjs";
import { ApiService } from "src/app/core/backend/api.service";
import { AuthStoreDispatcher } from "src/app/core/state/auth/auth.dispatcher";
import { ProfileStateDispatcher } from "src/app/core/state/profile/profile.dispatcher";
import { SopoLoadingService } from "../sopo-loading/sopo-loading.service";

declare const zE: any;

@Injectable({
  providedIn: 'root'
})
export class ZendeskService {
  private _renderer: Renderer2;
  private _enableZendesk: BehaviorSubject<boolean | null> = new BehaviorSubject(null);
  private _unreadMessages: BehaviorSubject<boolean | null> = new BehaviorSubject(null);
  private _authenticated: boolean = false;

  get enableZendesk() {
    return this._enableZendesk.value;
  }

  get enableZendesk$() {
    return this._enableZendesk.asObservable();
  }

  get unreadMessages$() {
    return this._unreadMessages.asObservable();
  }

  constructor(
    private renderFactory: RendererFactory2,
    private firestore: Firestore,
    @Inject(DOCUMENT) private _document: Document,
    private authStoreDispatcher: AuthStoreDispatcher,
    private apiService: ApiService,
    private profileStateDispatcher: ProfileStateDispatcher,
    private sopoLoadingService: SopoLoadingService,
  ) {
    this._renderer = renderFactory.createRenderer(null, null);
  }

  public async initialize(): Promise<void> {
    try {
      await firstValueFrom(this.authStoreDispatcher.getState()
        .pipe(
          filter(authState => authState.isAuthenticated && authState.userData !== null)
        )
      );

      const docReference = doc(this.firestore, 'config/zendesk');
      const docData = (await getDoc(docReference)).data();

      if (docData && docData.key) {
        const script = this._renderer.createElement('script');
        script.id = 'ze-snippet';
        script.type = 'text/javascript';
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${docData.key}`;
        script.text = ``;
        this._renderer.setAttribute(script, 'async', '');
        this._renderer.appendChild(this._document.body, script);
        this._enableZendesk.next(true);
      } else {
        throw new Error('Key is missing!');
      }
    } catch (error) {
      console.error('[Zendesk] - initialize', error);
      this._enableZendesk.next(false);
    }
  }

  public async logoutZendesk() {
    const enableZendesk = await firstValueFrom(this.enableZendesk$.pipe(filter(status => status !== null)));
    if (!enableZendesk) return;

    console.debug('[Zendesk] - logoutUser');
    this._authenticated = false;
    this._unreadMessages.next(false);
    zE("messenger", "logoutUser");
  }

  public async openSupportChat(): Promise<void> {
    this.sopoLoadingService.present('api');
    if (!this._authenticated) await this.authentication();
    zE("messenger", "open");
    this.sopoLoadingService.dismiss('api');
  }

  public async authentication(fromAuth?: boolean): Promise<void> {
    try {
      const enableZendesk = await firstValueFrom(this.enableZendesk$.pipe(filter(status => status !== null)));
      if (!enableZendesk) return;

      const profileData = await firstValueFrom(this.profileStateDispatcher.getProfileState().pipe(map(profileState => profileState.profileData)));

      if (fromAuth && !profileData.zendeskEndUserId) return;

      console.debug('[Zendesk] - authentication');
      const response = await this.apiService.getLoginToken(profileData.uid);

      this.loginZendesk(response.payload);
      this.unreadMessagesListener();
      this._authenticated = true;
    } catch (error) {
      console.error('[Zendesk] - authentication', error);
    }
  }

  private loginZendesk(token: string): void {
    console.debug(`[Zendesk] - loginUser`);
    zE( "messenger", "loginUser",
      function jwtCallback(callback) {
        callback(token);
      },
      function loginCallback(error) {
        if (error) {
          // Example error handling
          const { type, reason, message } = error;
          console.error(`[Zendesk] - loginCallback`, type, reason, message);
        }
      }
    )
  }

  private unreadMessagesListener(): void {
    if (this._unreadMessages.value != null) return;

    zE("messenger:on", "unreadMessages", (count: number) => {
      console.debug(`[Zendesk] - unreadMessages`, count);
      if (count > 0) this._unreadMessages.next(true);
      else if (count === 0 && this._unreadMessages.value) this._unreadMessages.next(false);
    })
  }

}
